import { Link, NavLink, Route, Routes, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Suspense, lazy, useState } from 'react';
import { api } from './lib/api.ts';
import { HomePage } from './pages/HomePage.tsx';
import { Logo } from './components/Logo.tsx';

/*
 * Routes are code-split so the landing page ships only what it needs. The home
 * page stays eager because it is the entry point and splitting it would just
 * add a round trip before first paint.
 */
const PlayerPage = lazy(() =>
  import('./pages/PlayerPage.tsx').then((m) => ({ default: m.PlayerPage })),
);
const MatchPage = lazy(() =>
  import('./pages/MatchPage.tsx').then((m) => ({ default: m.MatchPage })),
);
const LeaguePage = lazy(() =>
  import('./pages/LeaguePage.tsx').then((m) => ({ default: m.LeaguePage })),
);
const TeamPage = lazy(() => import('./pages/TeamPage.tsx').then((m) => ({ default: m.TeamPage })));
const DeveloperPage = lazy(() =>
  import('./pages/DeveloperPage.tsx').then((m) => ({ default: m.DeveloperPage })),
);
const ScoutDivisionPage = lazy(() =>
  import('./pages/ScoutDivisionPage.tsx').then((m) => ({ default: m.ScoutDivisionPage })),
);
const ScoutTeamPage = lazy(() =>
  import('./pages/ScoutTeamPage.tsx').then((m) => ({ default: m.ScoutTeamPage })),
);

function PlayerSearch() {
  const [value, setValue] = useState('');
  const navigate = useNavigate();

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    // Accept a raw account id or a full Dotabuff/OpenDota/Stratz profile URL.
    const match = /(\d{4,})/.exec(value.trim());
    // navigate() is async in React Router 7; mark the fire-and-forget.
    if (match?.[1]) void navigate(`/players/${match[1]}`);
  };

  return (
    <form onSubmit={submit} className="flex items-center gap-2">
      <input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Account ID or profile URL"
        aria-label="Search for a player by account ID"
        className="w-56 rounded-lg border border-border-subtle bg-surface-2 px-3 py-1.5 text-sm text-ink-0 placeholder:text-ink-2 focus:border-accent focus:outline-none"
      />
    </form>
  );
}

function Header() {
  const { data } = useQuery({ queryKey: ['me'], queryFn: api.me, staleTime: 300_000 });
  const user = data?.user ?? null;

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
      isActive ? 'bg-surface-2 text-ink-0' : 'text-ink-1 hover:text-ink-0'
    }`;

  return (
    <header className="sticky top-0 z-10 border-b border-border-subtle bg-surface-0/95 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center gap-4 px-6 py-3">
        <Link to="/" className="flex items-center gap-2 text-sm font-semibold tracking-tight">
          <Logo className="h-7 w-7 text-accent" />
          <span>
            Fountain<span className="text-accent">Forge</span>
          </span>
        </Link>

        <nav className="flex items-center gap-1">
          <NavLink to="/" end className={linkClass}>
            Home
          </NavLink>
          <NavLink to="/leagues" className={linkClass}>
            Leagues
          </NavLink>
          <NavLink to="/developers" className={linkClass}>
            API
          </NavLink>
        </nav>

        <div className="ml-auto flex items-center gap-3">
          <PlayerSearch />
          {user ? (
            <Link
              to={`/players/${user.accountId}`}
              className="flex items-center gap-2 rounded-lg border border-border-subtle bg-surface-2 px-2.5 py-1.5 text-sm"
            >
              {user.avatar && <img src={user.avatar} alt="" className="h-5 w-5 rounded" />}
              <span className="text-ink-1">{user.personaName ?? 'My profile'}</span>
            </Link>
          ) : (
            <a
              href="/api/auth/steam/login"
              className="rounded-lg bg-accent px-3 py-1.5 text-sm font-medium text-surface-0 hover:bg-accent/85"
            >
              Sign in with Steam
            </a>
          )}
        </div>
      </div>
    </header>
  );
}

export function App() {
  return (
    <div className="min-h-screen">
      <Header />
      <main className="mx-auto max-w-7xl px-6 py-8">
        <Suspense
          fallback={
            <div className="py-20 text-center text-sm text-ink-2">Loading…</div>
          }
        >
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/players/:accountId" element={<PlayerPage />} />
          <Route path="/matches/:matchId" element={<MatchPage />} />
          <Route path="/leagues" element={<LeaguePage />} />
          <Route path="/leagues/:leagueId" element={<LeaguePage />} />
          <Route path="/leagues/:leagueId/teams/:teamId" element={<TeamPage />} />
          {/*
            Same page, scoped to one league. Reached from a leaderboard, where
            the natural question is about this league rather than the player's
            whole history — and it links onward to the unscoped profile.
          */}
          <Route path="/leagues/:leagueId/players/:accountId" element={<PlayerPage />} />
          <Route path="/developers" element={<DeveloperPage />} />
          {/*
            Private, unlisted scout pack. Do not add a nav / footer / home /
            sitemap link here — people type the URL.
          */}
          <Route path="/scout/rd2l/s39/:divisionSlug" element={<ScoutDivisionPage />} />
          <Route
            path="/scout/rd2l/s39/:divisionSlug/teams/:teamSlug"
            element={<ScoutTeamPage />}
          />
          <Route
            path="*"
            element={
              <div className="py-20 text-center text-sm text-ink-2">
                Page not found. <Link to="/" className="text-accent">Go home</Link>.
              </div>
            }
          />
        </Routes>
        </Suspense>
      </main>
      <footer className="border-t border-border-subtle px-6 py-6 text-center text-xs text-ink-2">
        Match data from OpenDota. Not affiliated with Valve Corporation.
      </footer>
    </div>
  );
}
